<template>
  <div class="print-container">
    <!-- 打印按钮（仅屏幕显示） -->
    <div class="no-print">
      <el-button type="primary" @click="handlePrint">打印试卷</el-button>
      <el-button @click="handlePreview">打印预览</el-button>
      <div class="config-info">
        <span>共 {{ paper.topics ? paper.topics.length : 0 }} 道题</span>
      </div>
    </div>

    <!-- 打印区域 -->
    <div class="print-area">
      <div class="print-page">
        <!-- 试卷标题 -->
        <div class="paper-header">
          <h1 class="paper-title">{{ paper.title }}</h1>
        </div>

        <!-- 题目内容区域（单列布局） -->
        <div class="questions-container">
          <div
            v-for="(topic, index) in paper.topics"
            :key="topic.id"
            class="question-item"
          >
            <!-- 普通题目（type 1-4） -->
            <template v-if="topic.type !== 5">
              <!-- 题号 + 题型 + 题干 -->
              <div class="question-title">
                <span class="question-number">{{ index + 1 }}.</span>
                <span class="question-type">[{{ getQuestionType(topic.type) }}]</span>
                <span class="question-text">{{ topic.title }}</span>
              </div>

              <!-- 选项（横向排列） -->
              <div class="question-options">
                <span
                  v-for="(optionValue, optionKey) in topic.options"
                  :key="optionKey"
                  class="option-item"
                >
                  <span class="option-label">{{ optionKey }}.</span>
                  <span class="option-text">{{ optionValue }}</span>
                </span>
              </div>
            </template>

            <!-- 材料题（type = 5） -->
            <template v-else>
              <div class="material-question">
                <!-- 材料题标题 -->
                <div class="material-header">
                  <span class="question-number">{{ index + 1 }}.</span>
                  <span class="question-type">[{{ getQuestionType(topic.type) }}]</span>
                  <span class="question-text">{{ topic.title }}</span>
                </div>

                <!-- 材料内容 -->
                <div v-if="topic.material" class="material-content">
                  {{ topic.material }}
                </div>

                <!-- 子题列表 -->
                <div v-if="topic.parts && topic.parts.length > 0" class="sub-questions">
                  <div
                    v-for="(part, partIndex) in topic.parts"
                    :key="part.id"
                    class="sub-question-item"
                  >
                    <!-- 子题题号 + 题型 + 题干 -->
                    <div class="question-title">
                      <span class="question-number">({{ partIndex + 1 }})</span>
                      <span class="question-type">[{{ getQuestionType(part.type) }}]</span>
                      <span class="question-text">{{ part.title }}</span>
                    </div>

                    <!-- 子题选项 -->
                    <div class="question-options">
                      <span
                        v-for="(optionValue, optionKey) in part.options"
                        :key="optionKey"
                        class="option-item"
                      >
                        <span class="option-label">{{ optionKey }}.</span>
                        <span class="option-text">{{ optionValue }}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'A4PrintPaper',

  props: {
    // 试卷数据
    paper: {
      type: Object,
      required: true,
      default: () => ({
        title: '',
        topics: []
      })
    }
  },

  methods: {
    /**
     * 获取题型名称
     * @param {number} type - 题型编号
     * @returns {string} 题型名称
     */
    getQuestionType(type) {
      const typeMap = {
        1: '单选题',
        2: '多选题',
        3: '判断题',
        4: '不定项选择题',
        5: '材料题'
      };
      return typeMap[type] || '未知题型';
    },

    /**
     * 打印方法
     */
    handlePrint() {
      window.print();
    },

    /**
     * 打印预览
     */
    handlePreview() {
      this.$message.info('请使用浏览器的打印预览功能（Ctrl+P）');
      window.print();
    }
  }
};
</script>

<style scoped>
/* =====================
   屏幕显示样式
   ===================== */
.print-container {
  width: 100%;
  background: #f5f5f5;
  padding: 20px;
}

.no-print {
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.config-info {
  margin-top: 10px;
  color: #606266;
  font-size: 14px;
}

.print-area {
  background: white;
}

.print-page {
  background: white;
  margin: 0 auto;
  padding: 40px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  /* 屏幕预览时模拟 A4 竖向比例（210mm × 297mm） */
  width: 794px;
  min-height: 1123px;
}

/* =====================
   试卷内容样式
   ===================== */
.paper-header {
  text-align: center;
  margin-bottom: 30px;
  padding-bottom: 20px;
  border-bottom: 2px solid #333;
}

.paper-title {
  font-size: 28px;
  font-weight: bold;
  color: #333;
  margin: 0;
}

/* 题目容器（单列布局） */
.questions-container {
  width: 100%;
}

/* 单个题目样式 */
.question-item {
  /* 优先避免题目跨页，但允许自动分页 */
  break-inside: avoid-page;
  page-break-inside: avoid;
  padding: 15px;
  border-radius: 4px;
}

.question-title {
  display: flex;
  align-items: baseline;
  margin-bottom: 12px;
  line-height: 1.8;
}

.question-number {
  font-weight: bold;
  color: #2aacb2;
  margin-right: 8px;
  flex-shrink: 0;
  font-size: 16px;
}

.question-type {
  font-weight: bold;
  color: #2aacb2;
  margin-right: 8px;
  flex-shrink: 0;
  font-size: 14px;
}

.question-text {
  flex: 1;
  color: #333;
  font-size: 15px;
  line-height: 1.8;
}

/* 选项横向排列 */
.question-options {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
  margin-left: 30px;
}

.option-item {
  display: inline-flex;
  align-items: baseline;
  min-width: 45%;
}

.option-label {
  font-weight: bold;
  color: #666;
  margin-right: 6px;
  flex-shrink: 0;
}

.option-text {
  color: #555;
  font-size: 14px;
  line-height: 1.6;
}

/* =====================
   材料题样式
   ===================== */
.material-question {
  width: 100%;
}

.material-header {
  display: flex;
  align-items: baseline;
  line-height: 1.8;
}

.material-content {
  padding: 15px;
  margin: 15px 0 20px 30px;
  color: #333;
  font-size: 14px;
  line-height: 1.8;
  border-radius: 4px;
}

.sub-questions {
  margin-left: 20px;
}

.sub-question-item {
  margin-bottom: 20px;
  padding: 12px;
  background: white;
  border-radius: 4px;
}

/* =====================
   打印样式（A4 竖向）
   ===================== */
@media print {
  /* 1. 设置 A4 竖向纸张 */
  @page {
    size: A4 portrait; /* A4 竖向：210mm × 297mm */
    margin: 15mm; /* 上下左右各 15mm 边距 */
  }

  /* 2. 隐藏打印按钮 */
  .no-print {
    display: none !important;
  }

  /* 3. 重置容器样式 */
  .print-container {
    background: white;
    padding: 0;
    margin: 0;
  }

  .print-area {
    background: white;
  }

  /* 4. 打印页面样式 */
  .print-page {
    width: 100%;
    height: auto;
    margin: 0;
    padding: 0;
    box-shadow: none;
    background: white;
  }

  /* 5. 标题样式优化 */
  .paper-header {
    margin-bottom: 20px;
    padding-bottom: 15px;
    border-bottom: 2px solid #000;
  }

  .paper-title {
    font-size: 22px;
    color: #000;
  }

  /* 6. 题目容器（单列） */
  .questions-container {
    width: 100%;
  }

  /* 7. 题目样式（黑白打印优化） */
  .question-item {
    /* 优先避免跨页，但内容过长时允许自动分页 */
    break-inside: auto;
    page-break-inside: auto;
    margin-bottom: 18px;
    padding: 12px;
    background: white;
    border-radius: 0;
  }

  .question-title {
    margin-bottom: 10px;
  }

  .question-number {
    color: #000;
    font-weight: bold;
    font-size: 14px;
  }

  .question-type {
    color: #000;
    font-weight: bold;
    font-size: 12px;
  }

  .question-text {
    color: #000;
    font-size: 13px;
    line-height: 1.6;
  }

  /* 8. 选项打印样式 */
  .question-options {
    gap: 10px;
    margin-left: 25px;
  }

  .option-item {
    min-width: 45%;
  }

  .option-label {
    color: #000;
  }

  .option-text {
    color: #000;
    font-size: 12px;
    line-height: 1.5;
  }

  /* 9. 材料题打印样式 */
  .material-question {
    break-inside: auto;
    page-break-inside: auto;
  }

  .material-header {
    margin-bottom: 12px;
  }

  .material-content {
    background: #f5f5f5;
    padding: 12px;
    margin: 12px 0 15px 25px;
    color: #000;
    font-size: 12px;
    line-height: 1.6;
  }

  .sub-questions {
    margin-left: 15px;
  }

  .sub-question-item {
    margin-bottom: 15px;
    padding: 10px;
    background: white;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  /* 10. 移除屏幕预览的阴影和背景 */
  * {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
}
</style>
